import type { ReactNode } from 'react';
import { READOUT_LABEL, READOUT_VALUE, SECOND_LINE } from '@/components/seo/roles';

/**
 * The one "here's a number this tool computed" card for the public
 * calculators (cohesion audit S1). Before this, five widgets each hand-rolled
 * their own version — `Tier`, two near-duplicate `Metric`s, a `Stat` and a
 * `Bucket` — with three different value sizes (text-lg / text-2xl / text-3xl,
 * none of them `text-stat`), inconsistent label case/weight, and a hint line
 * three of the five dropped entirely. Same job, five grammars.
 *
 * Built on `READOUT_LABEL` / `READOUT_VALUE` from `components/seo/roles.ts` —
 * the same roles `KeyNumbers.tsx` uses for a page's headline figures — so a
 * calculator's result reads as the same kind of fact everywhere it appears.
 */

interface Props {
  label: string;
  value: ReactNode;
  hint?: string;
  /**
   * Marks the one recommended/primary card among siblings. A brighter,
   * raised surface — never colour: a readout never spends the accent hue
   * (Status-readout rule: the accent marks position in the flow, not a
   * reading of the data, and a status hue would claim this is an outcome,
   * which "the studio-standard tier" is not).
   */
  emphasized?: boolean;
  testId?: string;
}

export default function ToolMetricCard({ label, value, hint, emphasized = false, testId }: Props) {
  return (
    <div
      data-testid={testId}
      className={
        'rounded-lg border p-4 text-center ' +
        (emphasized
          ? 'border-[var(--app-border-bright)] bg-[var(--app-card)]'
          : 'border-[var(--app-border)] bg-[var(--app-bg)]')
      }
    >
      <div className={READOUT_LABEL}>{label}</div>
      <div className={READOUT_VALUE} data-testid={testId ? `${testId}-value` : undefined}>
        {value}
      </div>
      {hint && <div className={`mt-1 ${SECOND_LINE}`}>{hint}</div>}
    </div>
  );
}
